import React, { forwardRef, useEffect, useRef } from 'react';
import './GoogleSearchInput.css';
import useGoogleSearch from '../../hooks/useGoogleSearch';

interface IGoogleSearchInput extends React.HTMLAttributes<HTMLInputElement> {
    placeHolderText?: string
    showX?: boolean
    inputValue: string
    setInputValue: (val: string) => void
    onXClick: () => void
    closeSearchLabel?: string
    googleAutoComplete?: boolean
    resultsUrl?: string
    autocompleteFilter?: string
}

interface IconProps extends React.HTMLAttributes<HTMLElement> {
    className?: string;
    onClick?: () => void;
    fill?: string;
    label?: string;
}

export const CloseSearchIcon = ({ className, onClick, fill = 'white', tabIndex = -1, label }: IconProps) => (
    <span className={className} onClick={onClick} aria-label={label || 'Clear Search input'}
        tabIndex={tabIndex ?? -1}
        aria-hidden={tabIndex === undefined || tabIndex === null || tabIndex === -1}
        role='button'
        onKeyDown={(e) => { 
            if (e.key === 'Enter' || e.key === ' ') {
                onClick();
            }
        }}>
        <svg width='25' height='25' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path fill={fill} fillRule='evenodd' clipRule='evenodd' d='M3.60393 20.003C3.2134 20.3936 3.2134 21.0267 3.60393 21.4172C3.99445 21.8078 4.62762 21.8078 5.01814 21.4172L12.7638 13.6716L20.5095 21.4172C20.9001 21.8078 21.5332 21.8078 21.9237 21.4172C22.3143 21.0267 22.3143 20.3936 21.9237 20.003L14.178 12.2573L21.8728 4.56258C22.2633 4.17205 22.2633 3.53889 21.8728 3.14836C21.4823 2.75784 20.8491 2.75784 20.4586 3.14836L12.7638 10.8431L5.06907 3.14836C4.67855 2.75784 4.04538 2.75784 3.65486 3.14836C3.26434 3.53889 3.26434 4.17205 3.65486 4.56258L11.3496 12.2573L3.60393 20.003Z' />
        </svg>
    </span>
);


export const SearchIcon = forwardRef(({ className, onClick, fill = 'white', role = '', tabIndex = -1, label }: IconProps, fwdRef: any) => (
    <span ref={fwdRef} className={className} onClick={onClick} aria-label={label || 'Search Icon'} role={role} tabIndex={tabIndex}
        onKeyDown={(e) => { 
            if (e.key === 'Enter' || e.key === ' ') {
                onClick();
            }
        }}>
        <svg width='22' height='23' viewBox='0 0 22 23' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path fill={fill} fillRule='evenodd' clipRule='evenodd' d='M2.02539 9.27734C2.02539 5.68749 4.93554 2.77734 8.52539 2.77734C12.1152 2.77734 15.0254 5.68749 15.0254 9.27734C15.0254 12.8672 12.1152 15.7773 8.52539 15.7773C4.93554 15.7773 2.02539 12.8672 2.02539 9.27734ZM8.52539 0.777344C3.83097 0.777344 0.0253906 4.58292 0.0253906 9.27734C0.0253906 13.9718 3.83097 17.7773 8.52539 17.7773C10.4978 17.7773 12.3134 17.1055 13.7556 15.9782C13.3901 16.5578 13.4598 17.3331 13.9647 17.838L18.4647 22.338C19.0505 22.9238 20.0003 22.9238 20.5861 22.338C21.1718 21.7522 21.1718 20.8025 20.5861 20.2167L16.0861 15.7167C15.6339 15.2646 14.965 15.1614 14.414 15.4072C16.0236 13.8605 17.0254 11.6859 17.0254 9.27734C17.0254 4.58292 13.2198 0.777344 8.52539 0.777344Z' />
        </svg>
    </span>
));
SearchIcon.displayName = 'SearchIcon';

const GoogleSearchInput = forwardRef(({ ...props }: IGoogleSearchInput, fwdRef: any) => {
    const { googleSearch,  fetchQuery } = useGoogleSearch();
    const googleInputRef = useRef<HTMLInputElement | null>(null);
    const isAuthor = (window.location.host.indexOf('4502') > -1) || (window.location.host.indexOf('3000') > -1);
    const resultsUrl = isAuthor ? '/content/cob-corp-acquisition/us/en/' + props.resultsUrl + '.html' : '/' + props.resultsUrl;

    useEffect(() => {
        if (window.location.pathname.includes('search') && props.googleAutoComplete) {
            const urlParams = new URLSearchParams(window.location.search);
            const paramValue = urlParams.get('q');
            if (paramValue) {
                props.setInputValue(paramValue);
            }
        }
    }, []);

    useEffect(() => {
        const targetInput = document.getElementById('gsc-i-id1') as HTMLInputElement | null;
        if (targetInput && props.googleAutoComplete) {
            googleInputRef.current = targetInput;
            googleInputRef.current.value = props.inputValue;
            const event = new Event('input', { bubbles: true });
            googleInputRef.current.dispatchEvent(event);
        }
    }, [props.inputValue]);

    useEffect(() => {
        if (googleSearch && props.googleAutoComplete) {
            googleSearch.search.cse.element.render({
                div: 'google-search-input',
                tag: 'searchbox-only',
                gname: 'google-search-input',
                attributes: {
                    resultsUrl: props.resultsUrl ? resultsUrl : '#',
                    enableAutoComplete: props.googleAutoComplete ?? false,
                    enableHistory: false,
                    queryParameterName: 'q',
                    as_sitesearch: props.autocompleteFilter ?? '',
                },
            });
            if (document.getElementById('GoogleSearchInput') !== null && document.getElementsByClassName('gssb_e')[0] !== null) {
                document.getElementById('GoogleSearchInput')?.appendChild(document.getElementsByClassName('gssb_e')[0]!);
            }
        }
    }, [googleSearch]);

    return <div id={props.googleAutoComplete ? 'GoogleSearchInput' : ''} className={`GoogleSearchInput ${props.className ? props.className : ''}`}>
        <SearchIcon fill='#767676' className='GoogleSearchInput__searchIcon' />
        <input ref={fwdRef} autoComplete='off' id='google-search-input' placeholder={props.placeHolderText} value={props.inputValue}
            className='GoogleSearchInput__input'
            onChange={(e) => {
                props.setInputValue(e.target.value);
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter' && props.googleAutoComplete) {
                    fetchQuery('google-search-input', props.inputValue);
                } else if (e.key === 'Enter') {
                    if (props.inputValue) {
                        const url = `${resultsUrl}?q=${props.inputValue}`;
                        window.location.assign(url);
                    }
                }
            }}
            {...props} />
        {props.showX && <CloseSearchIcon label={props.closeSearchLabel} tabIndex={0} fill='#767676' className='GoogleSearchInput__x' onClick={props.onXClick} />}
    </div>;

});

export default GoogleSearchInput;